/**
 * The Customers page's words in one place: what each saved view and each band is called and by what rule,
 * and how a number is compared with the period before. A comparison says where the number came from
 * ("up 12% from $1,200"); it never makes a percentage out of nothing. Pure.
 */
import { planName } from '../insights/account-words';
import type {
  CustomerMover,
  CustomerSort,
  CustomerViewName,
  MoverBand,
  SalesMetric,
} from './customer-types';

export const viewWord: Record<CustomerViewName, string> = {
  all: 'Everyone',
  'call-today': 'Call today',
  'running-out': 'Running out',
  expansion: 'Expansion',
  slipping: 'Slipping',
  'needs-us': 'Needs us',
  overdue: 'Overdue',
  'not-activated': 'Not activated',
};

/** Each view's rule, in the contract's words (CustomerViewName). */
export const viewRule: Record<CustomerViewName, string> = {
  all: 'Every company the filters leave.',
  'call-today': 'Running out, past due or slipping.',
  'running-out': "Held for pages, or under 14 days of pages at the last 7 days' pace.",
  expansion: 'Read at least 30% more pages in the last 30 days than the 30 before.',
  slipping:
    'Has paid by card and read at least 15% fewer pages in the last 30 days than the 30 before.',
  'needs-us': 'Something needs us now: work that stopped, or an export that failed in 24 hours.',
  overdue: 'An open next step past its due day.',
  'not-activated': 'No export delivered 14 days after sign-up.',
};

export interface BandWords {
  /** The short name on the bar and in the key. */
  word: string;
  /** The rule behind it. */
  rule: string;
  /** A glyph, so a band never speaks by colour alone. */
  glyph: string;
}

/** The bands in the order the bar draws them: falling on the left, rising on the right, then the two without a change. */
export const bandWords: Record<MoverBand, BandWords> = {
  down50: { word: 'Down 50%+', rule: 'Read half as many pages, or fewer.', glyph: '⇊' },
  down15: { word: 'Down 15%+', rule: 'Read 15% to 50% fewer pages.', glyph: '↘' },
  steady: { word: 'Steady', rule: 'Within 15% of the 30 days before.', glyph: '→' },
  up15: { word: 'Up 15%+', rule: 'Read 15% to 50% more pages.', glyph: '↗' },
  up50: { word: 'Up 50%+', rule: 'Read half as many pages again, or more.', glyph: '⇈' },
  new: { word: 'New', rule: 'Pages now, none in the 30 days before.', glyph: '✦' },
  none: { word: 'No pages', rule: 'No pages in either 30 days.', glyph: '·' },
};

export const sortWord: Record<CustomerSort, string> = {
  name: 'Customer',
  paid: 'Card money',
  pages: 'Pages read',
  lastActive: 'Last active',
  signedUp: 'Signed up',
};

export const metricWord: Record<SalesMetric, string> = {
  revenue: 'Card money',
  newPaying: 'New paying',
  pages: 'Pages read',
};

/** "Free" for no package, else the package's name ("Enterprise" for `enterprise`). */
export function packageWord(plan: string | null): string {
  if (!plan || plan === 'free') return 'Free';
  return planName(plan);
}

export type ChangeTone = 'success' | 'danger' | 'muted';

export interface Change {
  /** "up 12% from $1,200", "down from 4", "the same as before", "none before". */
  text: string;
  tone: ChangeTone;
  /** ↑, ↓ or →. */
  arrow: string;
}

/**
 * A number against the period before, in words. From nothing it says so; from almost nothing (a base under
 * ten, or ten times over) it names the base and leaves the percentage out.
 */
export function changeOf(now: number, before: number, show: (n: number) => string): Change {
  if (before === 0) return fromNothing(now);
  if (now === before) return { text: 'the same as before', tone: 'muted', arrow: '→' };
  const from = `${percentWord(now, before)}from ${show(before)}`;
  return now > before
    ? { text: `up ${from}`, tone: 'success', arrow: '↑' }
    : { text: `down ${from}`, tone: 'danger', arrow: '↓' };
}

function fromNothing(now: number): Change {
  return now === 0
    ? { text: 'none before either', tone: 'muted', arrow: '→' }
    : { text: 'none before', tone: 'success', arrow: '↑' };
}

/** "12% " when the base is big enough for a percentage to tell anything; otherwise nothing. */
function percentWord(now: number, before: number): string {
  const percent = Math.round((Math.abs(now - before) / before) * 100);
  return before >= 10 && now < before * 10 && percent > 0 ? `${percent}% ` : '';
}

/** A mover's change in words: "up from 1,200", "down from 300", "new". */
export function moverWord(m: CustomerMover, show: (n: number) => string): string {
  if (m.previousPages === 0) return m.pages > 0 ? 'new' : 'no pages';
  if (m.pages === m.previousPages) return 'the same';
  return `${m.pages > m.previousPages ? 'up' : 'down'} from ${show(m.previousPages)}`;
}

/** 12,400 as "12.4k" and 2,300,000 as "2.3M", for an axis; small numbers whole. */
export function compact(n: number): string {
  const short = (v: number, unit: string): string =>
    `${v >= 100 ? Math.round(v) : Math.round(v * 10) / 10}${unit}`;
  if (n >= 1_000_000) return short(n / 1_000_000, 'M');
  return n >= 10_000 ? short(n / 1000, 'k') : Math.round(n).toLocaleString('en-US');
}

/** "Showing 51–100 of 1,204"; "Showing 0" for none. */
export function showingWord(offset: number, shown: number, total: number): string {
  if (total === 0 || shown === 0) return 'No companies';
  const n = (v: number): string => v.toLocaleString('en-US');
  return `Showing ${n(offset + 1)}–${n(offset + shown)} of ${n(total)}`;
}
